// Client API for Collavue Cloud Functions.
//
// Functions are deployed to:
//   https://us-central1-fluencertuber.cloudfunctions.net/<name>
//
// In development you can override via REACT_APP_FUNCTIONS_BASE.
import { auth, appCheck } from "./firebase";
import { getToken } from "firebase/app-check";

const FUNCTIONS_BASE =
  process.env.REACT_APP_FUNCTIONS_BASE ||
  "https://us-central1-fluencertuber.cloudfunctions.net";

async function authedFetch(name, body, opts = {}) {
  const user = auth.currentUser;
  if (!user) throw new Error("Sign in to continue.");
  const idToken = await user.getIdToken();
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${idToken}`,
  };
  try {
    const ac = await getToken(appCheck, false);
    if (ac?.token) headers["X-Firebase-AppCheck"] = ac.token;
  } catch {}

  const timeoutMs = Number(opts.timeoutMs || 175000);
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  let r;
  try {
    r = await fetch(`${FUNCTIONS_BASE}/${name}`, {
      method: "POST",
      headers,
      body: JSON.stringify(body || {}),
      signal: controller.signal,
    });
  } catch (e) {
    if (e?.name === "AbortError") {
      throw new Error(
        "Sync hit the server time limit. Partial results may still appear shortly—try a narrower date range or fewer platforms."
      );
    }
    if (e instanceof TypeError) {
      throw new Error(
        "Network request failed before the server responded. Please retry in a moment."
      );
    }
    throw e;
  } finally {
    clearTimeout(timeoutId);
  }

  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(data?.error || `Request failed (${r.status})`);
    err.code = data?.code;
    err.status = r.status;
    throw err;
  }
  return data;
}

export const analyzeWebsite = (url, campaignType = "business") =>
  authedFetch("collavueAnalyzeWebsite", { url, campaignType });

export const generateCampaignStrategy = (
  profile,
  campaignType = "business",
  campaignGoal = "sales"
) =>
  authedFetch("collavueGenerateCampaign", { profile, campaignType, campaignGoal });

export const syncCampaign = (campaignId) =>
  authedFetch("collavueSyncCampaign", { campaignId }, { timeoutMs: 175000 });

export const generateReply = (leadId, mode = "helpful", length = "short") =>
  authedFetch("collavueGenerateReply", { leadId, mode, length });

export const trainBrandVoice = (payload) =>
  authedFetch("collavueTrainBrandVoice", payload);

export const expandProfileText = (field, text, profile) =>
  authedFetch("collavueExpandProfileText", { field, text, profile });

export const deleteAccount = (confirmation) =>
  authedFetch("collavueDeleteAccount", { confirmation });

// Ask the lead assistant a natural-language question about the lead inbox
// ("find every lead that mentions pricing"). Returns { answer, leadIds,
// matchedCount, scannedCount, suggestedCommandName, creditsUsed }.
export const leadChat = (message, { campaignId, history } = {}) =>
  authedFetch(
    "collavueLeadChat",
    { message, campaignId: campaignId || null, history: history || [] },
    { timeoutMs: 60000 }
  );

// ---------- Public (unauthenticated) marketing endpoints ----------
// The demo booking form runs on the marketing site, where there is no signed-in
// user, so these skip the bearer token. The function itself is rate limited.
async function publicFetch(name, { method = "POST", body, query } = {}) {
  const url = new URL(`${FUNCTIONS_BASE}/${name}`);
  Object.entries(query || {}).forEach(([k, v]) => url.searchParams.set(k, v));

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);

  let r;
  try {
    r = await fetch(url.toString(), {
      method,
      headers: method === "GET" ? {} : { "Content-Type": "application/json" },
      body: method === "GET" ? undefined : JSON.stringify(body || {}),
      signal: controller.signal,
    });
  } catch (e) {
    if (e?.name === "AbortError") {
      throw new Error("That took too long. Please try again.");
    }
    throw new Error("Network request failed. Please retry in a moment.");
  } finally {
    clearTimeout(timeoutId);
  }

  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(data?.error || `Request failed (${r.status})`);
    err.code = data?.code;
    err.status = r.status;
    throw err;
  }
  return data;
}

/** Slots already claimed on a given YYYY-MM-DD. Returns { taken: string[] }. */
export const getBookedDemoSlots = (date) =>
  publicFetch("collavueBookDemo", { method: "GET", query: { date } });

/** Book a demo slot. Emails the Collavue team with the time and reason. */
export const bookDemo = (payload) =>
  publicFetch("collavueBookDemo", { body: payload });
